export interface ICampaignCertificationInstance {
  id: string
  name: string
  description: string
  startDate: number
  endDate: number
  defaultRevisor: string
  validationRequested: boolean
  daysOfActionDelay: number
  managerRevisorLogicApplied: boolean
  massiveActionEnabled: boolean
  approvalWorkflow: string
  everyDayDatasetComputationActive: boolean
  computeDataset: boolean
  completedItems: number
  totalItems: number
  status: string
  closedOn: number | null
  notes: string | null
  creationDate: number
  createdBy: string
  baoRevisor: boolean
  businessRolesIncluded: boolean
  type: string
}

export interface ICampaignCertificationResponse {
  pageNumber: number
  pageSize: number
  pageTotalNumber: number
  totalElements: number
  campaigns: ICampaignCertificationItem[]
}

export interface ICertificationInstance {
  id: string
  recertId: string
  entitlement: IEntitlementItem
  revisor: IRevisorItem
  status: CertificationItemState
  action: any
  closedOn: number | null
  notes: string | null
  setByAdmin: boolean
  identitySystemId: string
  creationDate: number
  subject: CampaignSubject
  subjectType?: CertificationItemType
}

export enum CertificationItemType {
  ACCOUNT = 'ACCOUNT',
  ENTITLEMENT = 'ENTITLEMENT',
  USER = 'USER',
  OU = 'OU',
  BUSINESS_ROLE = 'BUSINESS_ROLE',
  TECHNICAL_USER = 'TECHNICAL_USER',
  Robot = 'Robot',
  Service = 'Service',
}

export enum CertificationItemState {
  OPEN = 'OPEN',
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  PENDING_EXECUTION = 'PENDING_EXECUTION',
  CLOSED = 'CLOSED',
  MIXED = 'MIXED',
}

export type CampaignSubject = IOUItem | IBusinessRoleItem | IUserItem

export interface ICampaignSelectedItemBase {
  certificationItemTypology: CertificationItemType
  certificationItemId: string
  identitySystemId: string
  status?: CertificationItemState
}

export interface ICampaignSelectedEntitlement
  extends ICampaignSelectedItemBase {
  adminRoleRequired: string | null
  application_description: string | null
  application_id: string
  application_name: string
  bao: string | null
  baoFullName: string | null
  chainEntitlementsIds?: string[]
  chainEntitlements?: IEntitlementItem[]
  company: string | null
  entitlement_description: string | null
  entitlement_id: string
  entitlement_name: string
  isCritical: boolean
  monoProfilo: boolean
  showOnFrontend: boolean
  violationOfVisibility: any
  businessRole: boolean
}

export interface ICampaignSelectedSubject extends ICampaignSelectedItemBase {
  name: string
  description: string
  nAssociatedEntitlement: number
  nAssociatedUsers: number
}

export interface ICampaignSelectedOu extends ICampaignSelectedItemBase {
  name: string
  owner: string
  nAssociatedEntitlement: number
}

export interface ICampaignSelectedBusinessRole
  extends ICampaignSelectedItemBase {
  name: string
  description: string
  nAssociatedEntitlement: number
  nAssociatedUsers: number
}

export interface ICampaignSelectedUser extends ICampaignSelectedItemBase {
  userId: string
  name: string
  surname: string
  organizational_unit: string
}

export type ICampaignSelectedItem =
  | ICampaignSelectedEntitlement
  | ICampaignSelectedOu
  | ICampaignSelectedUser
  | ICampaignSelectedBusinessRole

export interface IRevisorItem {
  id: string
  code: string
  name: string
  surname: string
  organizational_unit_code: string
  organizational_unit: string
  resp_code: string
  resp_full_name: string | null
  type: string
  company: string
  sections: null
  roles: null
}

export interface ISupervisorsItem {
  user: IRevisorItem
  role: boolean
}

export interface IOUItem {
  ou: string
  identitySystemId: string
  name: string
  description: string
  ownercode: string
  catalog: any
  businessRoles: any
}

export interface IPerimeterItem {
  included: boolean
  type: string
  ou?: IOUItem
  application?: IApplicationItem
  businessRole?: IBusinessRoleItem
  entitlement?: IEntitlementItem
  user?: IUserItem
}

export interface IConfigurationItem {
  id: string
  type: string
  name: string
  description: string
  creationDate: number
  duration: number
  repeatAfter: number
  defaultRevisor: string
  validationRequested: boolean
  daysOfActionDelay: number
  managerRevisorLogicApplied: boolean
  massiveActionEnabled: boolean
  approvalWorkflow: string
  createdBy: string
  everyDayDatasetComputationActive: boolean
  supervisors: ISimpleSupervisor[]
  perimeter: ISimplePerimeter[]
  businessRolesIncluded: boolean
  baoRevisor: boolean
}

export interface ICampaignRevisor {
  code: string
  name: string
  surname: string
  completedItems: number
  totalItems: number
  progress: string
}

export interface ICampaignRevisorRaw {
  pageNumber: number
  pageSize: number
  pageTotalNumber: number
  totalElements: number
  revisors: ICampaignRevisor[]
}

export interface ISimpleSupervisor {
  supervisorId: string
  role: boolean
}

export interface ISimplePerimeter {
  type: string
  value: string
  included: boolean
  identitySystemId: string
}

export interface IApplicationItem {
  id: number
  identitySystemId: string
  name: string
  description: string
  monoprofilo: any
  secop_approv_catalogo: any
  bao_approv_catalogo: any
  organiz_approv_risk_catalogo: any
  compliance_approv_risk_catalogo: any
  secgov_approv_risk_catalogo: any
  risk_approv_catalogo: any
  secop_approv_account: any
  secgov_approv_account: any
}

export interface IBusinessRoleItem {
  codeBusinessRole: string
  identitySystemId: string
  name: string
  description: string
  ou: string
  entitlements: IEntitlementItem[]
}

export interface IEntitlementItem {
  entitlement_id: string
  identity_system_id: string
  entitlement_name: string
  entitlement_description: string | null
  application_id: string
  application_name: string
  application_description: string | null
  company: string | null
  isCritical: any
  isMonoprofilo: boolean | null
  showOnFrontend: boolean | null
  bao: any
  baoFullName?: string | null
  violationOfVisibility?: any
  adminRoleRequired?: any
  chainEntitlementsIds?: string[]
  chainEntitlements?: IEntitlementItem[]
  admin_role_required?: any
}

export interface IUserItem {
  identitySystemId: string
  id: string
  code: string
  name: string
  surname: string
  organizational_unit_code: string
  organizational_unit: string
  resp_code: string
  resp_full_name: string | null
  type: string
  company: string
  sections: any
  roles: any
}

export interface ICampaignCertificationItem {
  uniqueId: string
  type: string
  name: string
  endDate: number | string
  status: string
  requestor: string
  progress: string
}

export interface IAddedSupervisorsItem {
  id_user: string
  name: string
  surname: string
  code: string
  uidMaster: string
  organizational_unit: string
}

export interface IAddedUsersItem {
  id_user: string
  name: string
  surname: string
  uidMaster: string
  organizational_unit: string
  include: boolean
  identitySystemId?: string
}

export interface IAddedApplicationItem {
  id: number
  application_name: string
  identitySystemId: string
  include: boolean
}

export interface IAddedBrItemEntitlement {
  entitlementId: string
  identitySystemId: string
}
export interface IAddedBrItem {
  br_id: string
  name: string
  description: string
  ouCode: string
  isBR: boolean
  entitlements: IAddedBrItemEntitlement[]
  include: boolean
}
export interface IAddedTechnicalUserItem {
  application: string
  description: string
  identitySystemId: string
  code: string
  include: boolean
}

export const CERTIFICATION_STATUS_LIST = [
  {
    label: 'OPEN',
    value: 1,
  },
  {
    label: 'PENDING_VALIDATION',
    value: 2,
  },
  {
    label: 'PENDING_EXECUTION',
    value: 3,
  },
  {
    label: 'CLOSED',
    value: 4,
  },
  {
    label: 'MIXED',
    value: 5,
  },
]

export enum CertificationStatusType {
  OPEN = 1,
  PENDING_VALIDATION = 2,
  PENDING_EXECUTION = 3,
  CLOSED = 4,
  MIXED = 5,
}

export const CERTIFICATION_ITEM_STATUS_LIST = [
  {
    label: 'OPEN',
    value: 1,
  },
  {
    label: 'PENDING_VALIDATION',
    value: 2,
  },
  {
    label: 'PENDING_EXECUTION',
    value: 3,
  },
  {
    label: 'CLOSED',
    value: 4,
  },
]

export enum CertificationActionType {
  KEEP = 1,
  REVOKE = 2,
}

export const CERTIFICATION_ITEM_ACTION_LIST = [
  {
    label: 'KEEP',
    value: 1,
  },
  {
    label: 'REVOKE',
    value: 2,
  },
]

export interface IEditCertificationCampaign {
  id: string
  type:
    | 'USER'
    | 'ENTITLEMENT'
    | 'BUSINESS_ROLE'
    | 'OU'
    | 'ACCOUNT'
    | 'TECHNICAL_USER'
    | 'Robot'
    | 'Service'
  name: string
  description: string
  duration: number
  repeatAfter: number
  creationDate: number
  startDate: number
  endDate: number
  defaultRevisor: any
  validationRequested: boolean
  daysOfActionDelay: number
  massiveActionEnabled: boolean
  everyDayDatasetComputationActive: boolean
  createdBy: string
  approvalWorkflow: string
  supervisors: ISimpleSupervisor[]
  perimeter: ISimplePerimeter[]
  computeDataset: boolean
  managerRevisorLogicApplied: boolean
  businessRolesIncluded: boolean
  baoRevisor: boolean
}

export interface IEditCertificationReferenceObject {
  id: string
  name: string
  description: string
  startDate: number
  endDate: number
  defaultRevisor: string
  validationRequested: boolean
  daysOfActionDelay: number
  managerRevisorLogicApplied: boolean
  massiveActionEnabled: boolean
  approvalWorkflow: string
  everyDayDatasetComputationActive: boolean
  computeDataset: boolean
  businessRolesIncluded: boolean
  baoRevisor: boolean
  supervisors: ISimpleSupervisor[]
  perimeter: ISimplePerimeter[]
}
